<template>
  <div id="credit-card" v-if="waitedForStripeScriptLoaded" class='credit-card-inputs data-questions flex-box wrap' :class='{ complete }'>
    <div class="question-group card-number-container">
      <div class="gap-3x"></div>
      <div class="card-label">Card Number</div>
      <div class="gap-1x"></div>
      <div :class="['element-container', errors.number && 'error']">
        <card-number class="stripe-element card-number"
          ref='cardNumber'
          :stripe='stripe'
          :options='options'
          @change='number = $event.complete'
        />
      </div>
      <div class="card-error-message" v-if="errors.number">
        {{errors.number}}
      </div>
    </div>
    <div class="question-group expires">
      <div class="gap-3x"></div>
      <div class="card-label">Expiration Date</div>
      <div class="gap-1x"></div>
      <div :class="['element-container', errors.expiry && 'error']">
        <card-expiry class='stripe-element card-expiry'
          ref='cardExpiry'
          :stripe='stripe'
          :options='options'
          @change='expiry = $event.complete'
        />
      </div>
      <div class="card-error-message" v-if="errors.expiry || errors.cvc">
        {{errors.expiry}}
      </div>
    </div>
    <div class="question-group cvc">
      <div class="gap-3x"></div>
      <div class="card-label">CVC</div>
      <div class="gap-1x"></div>
      <div :class="['element-container', errors.cvc && 'error']">
        <card-cvc class='stripe-element card-cvc'
          ref='cardCvc'
          :stripe='stripe'
          :options='options'
          @change='cvc = $event.complete'
        />
      </div>
      <div class="card-error-message" v-if="errors.cvc || errors.expiry">
        {{errors.cvc}}
      </div>
    </div>

    <div class="question-group">
      <div class="gap-3x"></div>
      <div class="card-label">Zip Code</div>
      <div class="gap-1x"></div>
      <div :class="['element-container', errors.postalCode && 'error']">
        <PostalCode class='stripe-element postal-code'
          ref='postalCode'
          :stripe='stripe'
          :options='postalCodeOptions'
          @change='postalCode = $event.complete'
        />
      </div>
      <div class="card-error-message" v-if="errors.postalCode">
        {{errors.postalCode}}
      </div>
    </div>
  </div>
</template>

<script>
import { CardNumber, CardExpiry, CardCvc, PostalCode, createToken } from 'vue-stripe-elements-plus'
import _ from 'lodash';
import EventBus from '../EventBus.js'

export default {
  props: {
    stripe: {
      type: String,
    },
  },
  data () {
    var style = {
      base: {
        color: '#171717',
        lineHeight: '24px',
        fontSize: '16px',
        '::placeholder': {
          color: '#8e8e8e'
        }
      },
      invalid: {
        color: '#d00000',
        iconColor: '#d00000'
      }
    }
    return {
      complete: false,
      number: false,
      expiry: false,
      cvc: false,
      postalCode: false,
      errors: {},
      options: {
        style
      },
      waitedForStripeScriptLoaded: false,
      postalCodeOptions: {
        style,
        placeholder: '94105',
      }
    }
  },
  components: { CardNumber, CardExpiry, CardCvc, PostalCode },
  methods: {
    checkOut(requestPaymentMethod, couponCode) {
      _.each(['number', 'expiry', 'cvc', 'postalCode'], this.validateField);
      if (this.complete) {
        EventBus.$emit('payment-in-progress')
        var method = requestPaymentMethod || this.requestPayment
        createToken().then((data) => method(data, couponCode)).catch(() => {
          EventBus.$emit('payment-failed', null)
        });
      } else {
        EventBus.$emit('payment-not-ready')
      }
    },
    requestPayment(data, couponCode) {
      this.$requestStripePayment(
        data.token.id,
        this.$questionnaire,
        couponCode,
        () => EventBus.$emit('payment-suceeded'),
        (error) => EventBus.$emit('payment-failed', error),
      );
    },
    validateField(attribute) {
      if(!this[attribute]) {
        this.$set(this.errors, attribute, "Invalid")
      } else {
        this.$set(this.errors, attribute, null)
      }
    },
    update(attribute) {
      this.complete = this.number && this.expiry && this.cvc && this.postalCode
      this.validateField(attribute);
      // field completed, find field to focus next
      if (this.number) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus()
        } else if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.postalCode) {
          this.$refs.postalCode.focus()
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.number) {
          this.$refs.cardNumber.focus()
        }
      }
      // no focus magic for the CVC field as it gets complete with three
      // numbers, but can also have four
    },
    addStripeScript() {
      console.log('executed')
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
          this.waitedForStripeScriptLoaded = true
      };
      script.src = 'https://js.stripe.com/v3/';
      document.getElementsByTagName('head')[0].appendChild(script);
    },
  },
  watch: {
    number () { this.update('number') },
    expiry () { this.update('expiry') },
    cvc () { this.update('cvc') },
    postalCode () { this.update('postalCode') }
  },
  created(){
    EventBus.$on('checkout-cc', this.checkOut)
  },
  beforeDestroy() {
    EventBus.$off('checkout-cc', this.checkOut)
  },
  mounted() {
    this.addStripeScript()
  }
}
</script>

<style lang="scss">
#credit-card {
  .card-label {
    color: #979797;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .card-error-message {
    font-size: 13px;
    line-height: 16px;
    color: #d00000;
    margin-top: 4px;
  }

  .input-section {
    width: calc(25% - 60px);
    margin-right: 80px;
  }

  .input-section:nth-child(4) {
    margin-right: 0;
  }

  .element-container {
    height: 48px;
    padding: 12px 16px;
    color: #6d6d6d;
    background: white;
    font-size: 16px;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    border-radius: 4px;

    &.error {
      border-color: #d00000;
    }
  }

  @media screen and (max-width: 864px) {
    .element-container {
      width: 100%;
    }
  }
}
</style>
